/**
 * Parse scan data from various formats (CSV, TSV, Wireless Workbench, etc.)
 * @param {string} rawData - Raw text data to parse
 * @returns {Object} - { data: Array, skippedLines: number, totalLines: number, parseErrors: Array, isWorkbenchFormat: boolean }
 */
export function parseScanData(rawData) {
  const lines = rawData.trim().split("\n");
  const scanData = [];
  let skippedLines = 0;
  let parseErrors = [];
  let isWorkbenchFormat = false;
  
  // Detect Wireless Workbench format
  if (rawData.toLowerCase().includes('wireless workbench') || 
      rawData.includes('manufacturer') || 
      rawData.includes('freqName') ||
      rawData.includes('groupName')) {
    isWorkbenchFormat = true;
    return parseWorkbenchData(rawData);
  }
  
  for (let lineNum = 0; lineNum < lines.length; lineNum++) {
    const line = lines[lineNum];
    const cleanLine = line.trim();
    
    // Skip empty lines and comments
    if (!cleanLine || cleanLine.startsWith("#") || cleanLine.startsWith("//") || cleanLine.startsWith("'")) {
      continue;
    }
    
    // Skip header lines (contain "frequency", "MHz", "dBm" etc.)
    if (cleanLine.toLowerCase().includes("frequency") || 
        cleanLine.toLowerCase().includes("level") ||
        cleanLine.toLowerCase().includes("timestamp") ||
        /^[a-zA-Z,;\s]+$/.test(cleanLine)) {
      continue;
    }
    
    // Try to parse the line
    let freq = null;
    let level = null;
    
    // Format: RF Explorer with "Start: X MHz Stop: Y MHz"
    if (cleanLine.toLowerCase().includes('start:') || cleanLine.toLowerCase().includes('stop:')) {
      continue; // Skip config lines
    }
    // Format: kHz without decimal (e.g., "240725,-85" = 240.725 MHz)
    else if (/^\d{6,}[,;]\s*-?\d+/.test(cleanLine)) {
      const parts = cleanLine.split(/[,;]/).map(p => p.trim());
      if (parts.length >= 2) {
        const freqKhz = parseFloat(parts[0]);
        // If frequency is > 100000, it's in kHz, convert to MHz
        if (freqKhz > 100000) {
          freq = freqKhz / 1000;
        } else {
          freq = freqKhz;
        }
        level = parseFloat(parts[1]);
      }
    }
    // Format 1: CSV standard "560.000,-85.5" or "560.000;-85.5"
    else if (cleanLine.includes(",") && !cleanLine.includes(";")) {
      const parts = cleanLine.split(",").map(p => p.trim());
      if (parts.length >= 2) {
        freq = parseFloat(parts[0]);
        level = parseFloat(parts[1]);
      }
    }
    // Format 2: European CSV "560,000;-85,5" (semicolon separator, comma decimal)
    else if (cleanLine.includes(";")) {
      const parts = cleanLine.split(";").map(p => p.trim().replace(",", "."));
      if (parts.length >= 2) {
        freq = parseFloat(parts[0]);
        level = parseFloat(parts[1]);
      }
    }
    // Format 3: Tab separated "560.000\t-85.5"
    else if (cleanLine.includes("\t")) {
      const parts = cleanLine.split("\t").map(p => p.trim());
      if (parts.length >= 2) {
        freq = parseFloat(parts[0]);
        level = parseFloat(parts[1]);
      }
    }
    // Format 4: Space separated "560.000  -85.5" (2+ spaces)
    else if (/\s{2,}/.test(cleanLine)) {
      const parts = cleanLine.split(/\s+/).filter(p => p);
      if (parts.length >= 2) {
        freq = parseFloat(parts[0]);
        level = parseFloat(parts[1]);
      }
    }
    // Format 5: Simple space "560.000 -85.5"
    else if (cleanLine.includes(" ")) {
      const parts = cleanLine.split(/\s+/).filter(p => p);
      if (parts.length >= 2) {
        freq = parseFloat(parts[0]);
        level = parseFloat(parts[1]);
      }
    }
    // Format 6: RF Explorer format "560.000 MHz: -85.5 dBm"
    else if (line.toLowerCase().includes("mhz")) {
      const match = line.match(/([\d.,]+)\s*MHz[:\s]+([-\d.,]+)/i);
      if (match) {
        freq = parseFloat(match[1].replace(",", "."));
        level = parseFloat(match[2].replace(",", "."));
      }
    }
    
    // Validate and add
    if (freq !== null && level !== null && !isNaN(freq) && !isNaN(level)) {
      // Accept wider range for flexibility (200-1200 MHz covers most IEM bands)
      if (freq >= 200 && freq <= 1200) {
        scanData.push({
          frequency: freq,
          level: level,
          timestamp: new Date().toISOString()
        });
      } else {
        parseErrors.push(`Línea ${lineNum + 1}: Frecuencia fuera de rango (${freq} MHz)`);
        skippedLines++;
      }
    } else if (cleanLine.length > 0 && !isWorkbenchFormat) {
      parseErrors.push(`Línea ${lineNum + 1}: No se pudo parsear "${line.substring(0, 30)}..."`);
      skippedLines++;
    }
  }
  
  // Log parse errors for debugging
  if (parseErrors.length > 0 && parseErrors.length <= 5) {
    console.warn("Errores de parseo:", parseErrors);
  } else if (parseErrors.length > 5) {
    console.warn(`${parseErrors.length} errores de parseo. Primeros 5:`, parseErrors.slice(0, 5));
  }
  
  // Sort by frequency
  scanData.sort((a, b) => a.frequency - b.frequency);
  
  return {
    data: scanData,
    skippedLines,
    totalLines: lines.length,
    parseErrors,
    isWorkbenchFormat
  };
}

/**
 * Parse Wireless Workbench format data
 */
function parseWorkbenchData(rawData) {
  const lines = rawData.trim().split("\n");
  const scanData = [];
  let skippedLines = 0;
  const parseErrors = [];
  
  // Find the header line to get column indices
  let headerIdx = -1;
  let freqColIdx = -1;
  let levelColIdx = -1;
  
  for (let i = 0; i < Math.min(lines.length, 20); i++) {
    const lower = lines[i].toLowerCase();
    if (lower.includes('freq') || lower.includes('mhz') || lower.includes('frequency')) {
      headerIdx = i;
      const cols = lines[i].split(/[,\t;]/).map(c => c.trim().toLowerCase());
      freqColIdx = cols.findIndex(c => c.includes('freq') || c === 'mhz');
      levelColIdx = cols.findIndex(c => c.includes('level') || c.includes('dbm') || c.includes('power'));
      break;
    }
  }
  
  // If no level column found, just extract frequencies as placeholder levels
  if (freqColIdx === -1) {
    freqColIdx = 0;
  }
  
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const cols = line.split(/[,\t;]/).map(c => c.trim());
    if (cols.length <= freqColIdx) continue;
    
    const freqStr = cols[freqColIdx].replace(/[^\d.-]/g, '');
    const freq = parseFloat(freqStr);
    
    let level = -75; // Default level for Workbench format (they don't always have level)
    if (levelColIdx >= 0 && cols[levelColIdx]) {
      const levelStr = cols[levelColIdx].replace(/[^\d.-]/g, '');
      const parsedLevel = parseFloat(levelStr);
      if (!isNaN(parsedLevel)) {
        level = parsedLevel;
      }
    }
    
    if (!isNaN(freq) && freq >= 200 && freq <= 1200) {
      scanData.push({
        frequency: freq,
        level: level,
        timestamp: new Date().toISOString()
      });
    }
  }
  
  scanData.sort((a, b) => a.frequency - b.frequency);
  
  return {
    data: scanData,
    skippedLines,
    totalLines: lines.length,
    parseErrors,
    isWorkbenchFormat: true
  };
}

/**
 * Export scan data to CSV format
 * @param {Array} scanData - Array of {frequency, level} objects
 * @param {string} filename - Base filename (without extension)
 */
export function exportScanToCSV(scanData, filename = 'rf_scan') {
  if (!scanData || scanData.length === 0) {
    console.error('No data to export');
    return;
  }
  
  // Create CSV content
  const headers = 'Frequency (MHz),Level (dBm)\n';
  const rows = scanData.map(point => 
    `${point.frequency.toFixed(3)},${point.level.toFixed(1)}`
  ).join('\n');
  
  const csvContent = headers + rows;
  
  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default {
  parseScanData,
  exportScanToCSV
};
